// @tennis-matches/services/trainerstunden/team-captain-scope
// Mannschaftsführer: scoped TrainingsDesk (nur Freies Training, eigener Kader, Zeitfenster)

import { findFreeTrainingTrainer, isFreeTrainingTrainer } from "./free-training-trainer.js";

function __asStr(x) {
  return String(x ?? "").trim();
}

/** Standard-Zeitfenster: Mi & Fr 18–20 Uhr */
export const DEFAULT_TEAM_CAPTAIN_PLANNING_SLOTS = Object.freeze([
  { weekday: 3, startHm: "18:00", endHm: "20:00" },
  { weekday: 5, startHm: "18:00", endHm: "20:00" },
]);

/**
 * @param {unknown} viewer
 */
export function isTeamCaptainViewer(viewer) {
  return __asStr(viewer?.mode).toLowerCase() === "team_captain";
}

/**
 * @param {unknown[]} trainers
 */
export function filterTrainersForTeamCaptain(trainers) {
  const free = findFreeTrainingTrainer(trainers);
  return free ? [free] : [];
}

/**
 * @param {unknown[]} people
 * @param {unknown[]|Set<string>} squadPersonIds
 */
export function filterPeopleForTeamCaptain(people, squadPersonIds) {
  const ids =
    squadPersonIds instanceof Set
      ? squadPersonIds
      : new Set(
          (Array.isArray(squadPersonIds) ? squadPersonIds : [])
            .map((id) => __asStr(id))
            .filter(Boolean)
        );
  if (!ids.size) return [];
  return (people || []).filter((p) => ids.has(__asStr(p?.id)));
}

/**
 * @param {unknown[]} sessions
 * @param {unknown[]} trainers
 * @param {string} [authUserId]
 */
export function filterSessionsForTeamCaptain(sessions, trainers, authUserId = "") {
  const uid = __asStr(authUserId);
  const freeTrainerId = __asStr(findFreeTrainingTrainer(trainers)?.id);
  if (!freeTrainerId) return [];

  return (sessions || []).filter((s) => {
    if (__asStr(s?.trainer_id) !== freeTrainerId) return false;
    if (uid && __asStr(s?.created_by) !== uid) return false;
    return true;
  });
}

/**
 * @param {unknown} session
 * @param {unknown[]} trainers
 */
export function isSessionFreeTraining(session, trainers) {
  const trainerId = __asStr(session?.trainer_id);
  if (!trainerId) return false;
  const trainer = (trainers || []).find((tr) => __asStr(tr?.id) === trainerId);
  return isFreeTrainingTrainer(trainer);
}

function __weekdayFromYmd(ymd) {
  const s = __asStr(ymd).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  if (!Number.isFinite(dt.getTime())) return null;
  return dt.getDay();
}

function __hmToMinutes(hm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(__asStr(hm));
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mi)) return null;
  return h * 60 + mi;
}

/**
 * @param {{
 *   dateYmd?: string,
 *   startHm?: string,
 *   endHm?: string,
 *   durationMin?: number,
 *   slots?: Array<{ weekday?: number, startHm?: string, endHm?: string }>,
 * }} input
 * @returns {{ ok: boolean, reason?: string }}
 */
export function validateTeamCaptainPlanningSlot(input = {}) {
  const dateYmd = __asStr(input.dateYmd).slice(0, 10);
  const startHm = __asStr(input.startHm).slice(0, 5);
  const endHmRaw = __asStr(input.endHm).slice(0, 5);
  const durationMin = Math.max(0, Number(input.durationMin) || 0);
  const slots = Array.isArray(input.slots) && input.slots.length
    ? input.slots
    : DEFAULT_TEAM_CAPTAIN_PLANNING_SLOTS;

  const weekday = __weekdayFromYmd(dateYmd);
  const startMin = __hmToMinutes(startHm);
  if (weekday == null || startMin == null) {
    return { ok: false, reason: "invalid-datetime" };
  }

  let endMin = endHmRaw ? __hmToMinutes(endHmRaw) : null;
  if (endMin == null && durationMin > 0) {
    endMin = startMin + durationMin;
  }
  if (endMin == null) {
    return { ok: false, reason: "invalid-end" };
  }

  const allowed = slots.some((slot) => {
    if (Number(slot?.weekday) !== weekday) return false;
    const slotStart = __hmToMinutes(slot?.startHm);
    const slotEnd = __hmToMinutes(slot?.endHm);
    if (slotStart == null || slotEnd == null) return false;
    return startMin >= slotStart && endMin <= slotEnd;
  });

  return allowed ? { ok: true } : { ok: false, reason: "outside-allowed-slot" };
}

/**
 * Nächstes erlaubtes MF-Zeitfenster (Mi/Fr 18:00) ab {@link now}.
 * @param {Date} [now]
 * @returns {Date}
 */
export function resolveTeamCaptainDefaultPlanningStart(now = new Date()) {
  const ref = now instanceof Date && Number.isFinite(now.getTime()) ? now : new Date();
  const slots = DEFAULT_TEAM_CAPTAIN_PLANNING_SLOTS;
  const startHm = __asStr(slots[0]?.startHm || "18:00").slice(0, 5);
  const startMin = __hmToMinutes(startHm) ?? 18 * 60;

  for (let offset = 0; offset < 14; offset += 1) {
    const dt = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() + offset, 12, 0, 0);
    const weekday = dt.getDay();
    const slot = slots.find((s) => Number(s?.weekday) === weekday);
    if (!slot) continue;

    const candidate = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), Math.floor(startMin / 60), startMin % 60, 0, 0);
    if (offset === 0 && candidate.getTime() < ref.getTime()) {
      const snapped = new Date(ref);
      snapped.setSeconds(0, 0);
      const snappedMin = snapped.getHours() * 60 + snapped.getMinutes();
      const slotEnd = __hmToMinutes(slot?.endHm) ?? 20 * 60;
      if (snappedMin < slotEnd) {
        const useMin = Math.max(startMin, snappedMin);
        return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), Math.floor(useMin / 60), useMin % 60, 0, 0);
      }
      continue;
    }
    return candidate;
  }

  const fallback = new Date(ref);
  fallback.setHours(18, 0, 0, 0);
  return fallback;
}

/**
 * Freies Training: keine Durchführungsbestätigung / Abrechnung.
 * @param {unknown} session
 * @param {unknown[]} trainers
 */
export function isSessionAttendanceConfirmDisabled(session, trainers) {
  return isSessionFreeTraining(session, trainers);
}
